import { useMenuState } from '@chakra-ui/react';
import { GroupBase, MultiValue, SelectInstance, SingleValue } from 'chakra-react-select';
import { useCallback, useRef } from 'react';

import { Combobox, ComboboxProps } from '../combobox';
import { useMenuSelectContext } from './menu-select';

type MenuComboboxProps<
  TOption extends object,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
> = Omit<
  ComboboxProps<TOption, false, TGroup>,
  'autoFocus' | 'menuIsOpen' | 'onChange' | 'size' | 'variant'
> & {
  closeOnSelect?: boolean;
  onChange: (option: SingleValue<TOption>) => void;
};

export function MenuCombobox<
  TOption extends object,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
>({ closeOnSelect = true, onChange, ...props }: MenuComboboxProps<TOption, TGroup>) {
  const { isOpen, onClose } = useMenuState();
  const menuSelect = useMenuSelectContext();
  const focusTimeoutRef = useRef<number | null>(null);
  const setInitialFocusRef = useCallback(
    (instance: SelectInstance<TOption, false, TGroup> | null) => {
      if (menuSelect) {
        menuSelect.initialFocusRef.current = instance;
      }

      if (focusTimeoutRef.current !== null) {
        window.clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }

      if (instance && isOpen) {
        // Chakra schedules menu focus on open; focus React Select after that handoff.
        focusTimeoutRef.current = window.setTimeout(() => {
          instance.focus();
          focusTimeoutRef.current = null;
        });
      }
    },
    [isOpen, menuSelect],
  );

  return (
    <Combobox<TOption, false, TGroup>
      variant="compact"
      size="sm"
      menuIsOpen
      ref={setInitialFocusRef}
      {...props}
      onChange={(option) => {
        onChange(option);
        if (closeOnSelect) {
          onClose();
        }
      }}
    />
  );
}

type MenuMultiComboboxProps<
  TOption extends object,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
> = Omit<
  ComboboxProps<TOption, true, TGroup>,
  'autoFocus' | 'isMulti' | 'menuIsOpen' | 'onChange' | 'size' | 'variant'
> & {
  onChange: (options: TOption[]) => void;
};

export function MenuMultiCombobox<
  TOption extends object,
  TGroup extends GroupBase<TOption> = GroupBase<TOption>,
>({ onChange, ...props }: MenuMultiComboboxProps<TOption, TGroup>) {
  const { isOpen } = useMenuState();
  const menuSelect = useMenuSelectContext();
  const focusTimeoutRef = useRef<number | null>(null);
  const setInitialFocusRef = useCallback(
    (instance: SelectInstance<TOption, true, TGroup> | null) => {
      if (menuSelect) {
        menuSelect.initialFocusRef.current = instance;
      }

      if (focusTimeoutRef.current !== null) {
        window.clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }

      if (instance && isOpen) {
        // Chakra schedules menu focus on open; focus React Select after that handoff.
        focusTimeoutRef.current = window.setTimeout(() => {
          instance.focus();
          focusTimeoutRef.current = null;
        });
      }
    },
    [isOpen, menuSelect],
  );

  return (
    <Combobox<TOption, true, TGroup>
      variant="compact"
      size="sm"
      menuIsOpen
      isMulti
      ref={setInitialFocusRef}
      {...props}
      onChange={(options: MultiValue<TOption>) => onChange([...options])}
    />
  );
}
